'use client';

import React, { useEffect, useState } from 'react';
import { Icon, Spinner } from '@/src/components/ui';
import { noBorderStyles } from '@/src/constants/constants';
import { Input } from '@workspace/ui/components/input';

type TenorMediaFormat = { url: string; dims?: [number, number] };
type TenorApiGif = {
  id: string;
  title?: string;
  content_description?: string;
  media_formats: Record<string, TenorMediaFormat>;
};
type TenorResponse = { results: TenorApiGif[] };

export type TenorGif = {
  id: string;
  title: string;
  previewUrl: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (gif: TenorGif) => void;
};

const API_KEY = process.env.NEXT_PUBLIC_TENOR_GIF_API_KEY!;
const TENOR_BASE = 'https://tenor.googleapis.com/v2';
const CLIENT_KEY = 'player3_web';
const LIMIT = 40;
const CONTENT_FILTER: 'low' | 'medium' | 'high' | 'off' = 'medium';


const PREVIEW_FORMATS = ['tinygif', 'nanogif', 'gif'] as const;

function mapTenorItem(x: TenorApiGif): TenorGif | null {
  const mf = x.media_formats ?? {};
  const media = PREVIEW_FORMATS.map((k) => mf[k]).find((m) => m?.url);
  if (!media?.url) return null;

  return {
    id: x.id,
    title: x.content_description || x.title || 'GIF',
    previewUrl: media.url,
  };
}

export const GifPickerModal = ({ open, onClose, onSelect }: Props) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TenorGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGifs = async (search: string) => {
    if (!open) return;

    try {
      setLoading(true);
      setError(null);

      const cf = `&contentfilter=${encodeURIComponent(CONTENT_FILTER)}`;

      const url =
        search.trim().length === 0
          ? `${TENOR_BASE}/featured?key=${encodeURIComponent(API_KEY)}&client_key=${encodeURIComponent(
              CLIENT_KEY
            )}&limit=${LIMIT}${cf}`
          : `${TENOR_BASE}/search?q=${encodeURIComponent(search)}&key=${encodeURIComponent(
              API_KEY
            )}&client_key=${encodeURIComponent(CLIENT_KEY)}&limit=${LIMIT}${cf}`;


      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load GIFs');

      const json = (await res.json()) as TenorResponse;
      const mapped = (json.results ?? [])
        .map(mapTenorItem)
        .filter(Boolean) as TenorGif[];

      setResults(mapped);
    } catch (e) {
      console.error(e);
      setError('Something went wrong while loading GIFs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setQuery('');
      fetchGifs('');
    }
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (open) fetchGifs(query);
    }, 300);

    return () => clearTimeout(id);
  }, [query, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-[650px] max-h-[80vh] flex flex-col">
        <div className="px-9 py-5 border-b flex items-center gap-10">
          <Icon
            name="close"
            className="size-4 hover:cursor-pointer"
            onClick={onClose}
          />
          <div className="flex-1 border rounded-md flex items-center">
            <div className="bg-gray-100 p-3 overflow-hidden rounded-l-md border-r">
              <Icon name="magnifying-glass" className="size-4 text-gray-400" />
            </div>
            <Input
              type="text"
              style={noBorderStyles}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for gifs..."
              className="p-4! text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading && (
            <div className="text-center text-sm">
              <Spinner />
            </div>
          )}
          {error && (
            <div className="text-center text-xs text-red-500 mb-2">{error}</div>
          )}

          <div className="grid md:grid-cols-4 grid-cols-3 gap-1">
            {results.map((gif) => (
              <button
                key={gif.id}
                type="button"
                className="relative group"
                onClick={() => onSelect(gif)}
              >
                <img
                  src={gif.previewUrl}
                  alt={gif.title}
                  className="size-40 rounded-md object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          {!loading && results.length === 0 && (
            <div className="text-center text-xs text-gray-500 mt-4">
              Nothing found
            </div>
          )}
        </div>
        <img src={'/icons/tenor.png'} className="m-3 h-3 w-fit" />
      </div>
    </div>
  );
};
